<template>
  <b-modal :active="show" has-modal-card>
    <div class="modal-card">
      <header class="modal-card-head">
        <p class="modal-card-title">Edit Location</p>
      </header>
      <section class="modal-card-body" >

        <b-loading :active="isFetching" :is-full-page="false" />

        <template v-if="!isFetching">
          <b-field label="Company or Facility Name">
            <b-input v-model="locationName"/>
          </b-field>
          <address-form v-model="address" />
          <hr/>
          <contact-form v-model="contacts" :can-delete="true" :remove-key-on-update="false" @deleted="trackDeletedContact" />
        </template>

      </section>
      <footer class="modal-card-foot columns">
        <div class="column is-one-third">
          <button class="button" type="button" @click="close" >Cancel</button>
          <button class="button" type="button" @click="save" :class="{ 'is-loading': isSaving }" >Save</button>
        </div>
      </footer>
    </div>
  </b-modal>
</template>

<script>
import RolodexApi from '@/api-interface/rolordex'
import { isEmpty } from 'lodash-es'
import contactToRolodex from '@/helpers/convertContactToRolodex'

import AddressForm from '@/components/AddressLookup'
import ContactForm from '@/components/ContactFrom-Multiple'

export default {
  name: 'RolodexLocationEdit',

  props: {
    show: {
      type: Boolean,
      required: true
    },
    locationKey: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      isFetching: false,
      isSaving: false,
      rawDetails: {},
      locationName: '',
      address: {},
      contacts: [],
      deleteQueue: []
    }
  },

  components: {
    AddressForm,
    ContactForm
  },

  computed: {
    addressKey() {
      return this.rawDetails.companyRolodexJobLocationAddresses[0].companyRolodexJobLocationAddressKey
    },

    payload() {
      const address = {
        ...this.rawDetails.companyRolodexJobLocationAddresses[0],
        companyRolodexJobLocationAddressStreet1: this.address.street1,
        companyRolodexJobLocationAddressStreet2: this.address.street2,
        companyRolodexJobLocationAddressZip: this.address.zip,
        companyRolodexJobLocationAddressCity: this.address.city,
        companyRolodexJobLocationAddressState: this.address.state
      }

      address.companyRolodexContacts = this.existingContacts

      return {
        ...this.rawDetails,
        companyRolodexJobLocationName: this.locationName,
        companyRolodexJobLocationAddresses: [address]
      }
    },

    newContacts() {
      return this.contacts
        .filter((contact) => isEmpty(contact.companyRolodexContactKey))
        .map(contactToRolodex)
        .map((contact) => {
          delete contact.companyRolodexContactKey
          return contact
        })
    },

    existingContacts() {
      return this.contacts.filter((contact) => !isEmpty(contact.companyRolodexContactKey)).map(contactToRolodex)
    }
  },

  methods: {
    close() {
      this.$emit('update:show', false)
    },

    async save() {
      this.isSaving = true
      try {
        await Promise.all([
          RolodexApi.updateLocation(this.payload),
          this._updateExistingContact(),
          this._deleteContacts(),
          this._createContacts()
        ])
      } catch (error) {
        console.error('Enable to update location', error)
      } finally {
        this.isSaving = false
        this.$emit('updated')
        this.close()
      }
    },

    async _deleteContacts() {
      return Promise.all(this.deleteQueue.map(RolodexApi.deleteContactByKey))
    },

    async _updateExistingContact() {
      return Promise.all(this.existingContacts.map(RolodexApi.updateContact))
    },

    async _createContacts() {
      return Promise.all(
        this.newContacts.map((contact) =>
          RolodexApi.addContact({ contactInfo: contact, addressKeys: [this.addressKey] })
        )
      )
    },

    trackDeletedContact(contact) {
      if (contact.hasOwnProperty('companyRolodexContactKey')) {
        this.deleteQueue.push(contact.companyRolodexContactKey)
      }
    },

    transformRaw() {
      this.locationName = this.rawDetails.companyRolodexJobLocationName

      const rawAddress = this.rawDetails.companyRolodexJobLocationAddresses[0]
      this.address = {
        street1: rawAddress.companyRolodexJobLocationAddressStreet1,
        street2: rawAddress.companyRolodexJobLocationAddressStreet2,
        zip: rawAddress.companyRolodexJobLocationAddressZip,
        state: rawAddress.companyRolodexJobLocationAddressState,
        city: rawAddress.companyRolodexJobLocationAddressCity
      }

      this.contacts = rawAddress.companyRolodexContacts.map((contact) => ({
        ...contact,
        name: contact.companyRolodexContactFullName,
        email: contact.companyRolodexContactEmailAddress,
        phone: contact.companyRolodexContactPhones[0]
          ? contact.companyRolodexContactPhones[0].companyRolodexContactPhoneDigits
          : ''
      }))
    },

    async fetchDetails() {
      this.isFetching = true
      try {
        this.rawDetails = await RolodexApi.getLocationByKey({ companyRolodexJobLocationKey: this.locationKey })
        this.transformRaw()
      } catch (error) {
        console.error(`Enable to load location details - ${this.locationKey}`, error)
      } finally {
        this.isFetching = false
      }
    }
  },

  watch: {
    show() {
      if (this.show) {
        this.fetchDetails()
      }
    }
  }
}
</script>

<style scoped>
.modal-card {
  width: 960px;
}
</style>
